import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import PostCreateGalleri from "@/components/PostCreateGalleri";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

export default function ModCreateEvent() {
  const navigate = useNavigate();
  // Etappe 4.2 — defaulta andrad_av till inloggad användare
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
  const [form, setForm] = useState({
    titel: "", beskrivning: "", datum: "", datum_slut: "", tid: "", tid_slut: "", plats: "",
    arrangor: "", kontakt_epost: "", kontakt_telefon: "", publish: true,
    andrad_av: (user?.name || user?.email || "").trim(),
    andringsnotering: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null); // Build 96.21 — post-create galleri

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.titel.trim()) e.titel = "Ange en titel";
    if (!form.beskrivning.trim()) e.beskrivning = "Ange en beskrivning";
    if (!form.datum) e.datum = "Ange datum";
    if (!form.plats.trim()) e.plats = "Ange plats";
    // Build 94c.4 — datums-/tids-validering
    if (form.datum_slut && form.datum_slut < form.datum) e.datum_slut = "Slutdatum får inte vara tidigare än startdatum.";
    if (form.tid && form.tid_slut && (!form.datum_slut || form.datum_slut === form.datum) && form.tid_slut < form.tid) e.tid_slut = "Sluttid får inte vara tidigare än starttid (samma dag).";
    // Etappe 4.2 — Ändrad av Pflicht
    if (!form.andrad_av.trim()) e.andrad_av = "Ange namn på den som ändrar posten innan du sparar.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/mod/evenemang/create`, {
        titel: form.titel, beskrivning: form.beskrivning, datum: form.datum,
        datum_slut: form.datum_slut || null,
        tid: form.tid || null, tid_slut: form.tid_slut || null,
        plats: form.plats, arrangor: form.arrangor || null,
        kontakt_epost: form.kontakt_epost || null, kontakt_telefon: form.kontakt_telefon || null,
        publish: form.publish,
        andrad_av: form.andrad_av.trim(),
        andringsnotering: form.andringsnotering || null,
      }, { headers: modHeaders() });
      // Build 96.21 — istället för att navigera direkt, visa post-create galleri-uppladdning.
      setSavedId(res.data.id);
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
      else setErrors({ submit: err?.response?.data?.detail || "Något gick fel" });
    }
    setBusy(false);
  };

  if (savedId) {
    return (
      <div data-testid="mod-create-event-success" className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <BackLink to="/mod" label="Moderatorvy" />
        <PostCreateGalleri
          collection="evenemang"
          itemId={savedId}
          titel={form.titel}
          backTo="/mod"
          backLabel="Tillbaka till moderatorvyn"
          publikt={form.publish}
        />
      </div>
    );
  }

  return (
    <div data-testid="mod-create-event-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod/evenemang" />
      <h1 className="font-heading text-2xl font-medium text-ink mb-2">Skapa nytt evenemang</h1>
      <p className="text-base text-muted-foreground mb-6">Lägg in ett evenemang direkt.</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field label="Titel" error={errors.titel} required>
          <input data-testid="mod-event-titel" value={form.titel} onChange={e => set("titel", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Beskrivning" error={errors.beskrivning} required>
          <textarea data-testid="mod-event-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={3} className="input-mod" />
        </Field>
        <Field label="Startdatum" error={errors.datum} required>
          <input data-testid="mod-event-datum" type="date" value={form.datum} onChange={e => set("datum", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Slutdatum (valfritt, för flerdagars)" error={errors.datum_slut}>
          <input data-testid="mod-event-datum-slut" type="date" value={form.datum_slut} onChange={e => set("datum_slut", e.target.value)} className="input-mod" />
          <p className="text-xs text-bark/60 mt-1">Lämna tomt för endagsevenemang. Vid period: använd start- och slutdatum (t.ex. 2026-07-01 → 2026-07-31).</p>
        </Field>
        <Field label="Starttid (valfritt)">
          <input data-testid="mod-event-tid" type="time" value={form.tid} onChange={e => set("tid", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Sluttid (valfritt)" error={errors.tid_slut}>
          <input data-testid="mod-event-tid-slut" type="time" value={form.tid_slut} onChange={e => set("tid_slut", e.target.value)} className="input-mod" />
          <p className="text-xs text-bark/60 mt-1">Fri text som "Måndag–torsdag" hör hemma i titel eller beskrivning, inte i tidfältet.</p>
        </Field>
        <Field label="Plats" error={errors.plats} required>
          <input data-testid="mod-event-plats" value={form.plats} onChange={e => set("plats", e.target.value)} placeholder="T.ex. Backe bygdegård" className="input-mod" />
        </Field>
        <Field label="Arrangör">
          <input value={form.arrangor} onChange={e => set("arrangor", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Kontaktväg (valfritt)">
          <input value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} placeholder="E-post" className="input-mod mb-2" />
          <input value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} placeholder="Telefon" className="input-mod" />
        </Field>
        {/* Etappe 4.2 — Pflicht-Audit-Felder */}
        <Field label="Ändrad av" error={errors.andrad_av} required>
          <input data-testid="mod-event-andrad-av" value={form.andrad_av} onChange={e => set("andrad_av", e.target.value)} placeholder="Ange namn på den som ändrar posten" className="input-mod" />
        </Field>
        <Field label="Ändringsnotering (valfritt)">
          <input value={form.andringsnotering} onChange={e => set("andringsnotering", e.target.value)} placeholder="Kort kommentar om vad som ändrades" className="input-mod" />
        </Field>

        <div className="flex items-center gap-3">
          <input type="checkbox" checked={form.publish} onChange={e => set("publish", e.target.checked)} className="w-5 h-5 accent-pine" />
          <label className="text-base text-ink">Publicera direkt</label>
        </div>

        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}

        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={busy} data-testid="mod-event-submit" className="bg-pine hover:bg-pine-hover text-white font-medium px-6 py-3 rounded-md text-base transition-colors disabled:opacity-50">
            {busy ? "Skapar..." : form.publish ? "Skapa och publicera" : "Spara som utkast"}
          </button>
          <button type="button" onClick={() => navigate("/mod")} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
